import { useCallback } from 'react'
import { trackGoal } from '@lunary/ui-web/analytics'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ThemeName } from '@lunary/ui-tokens'
import { api } from '../../shared/api/client'
import { useRefreshLunars } from '../lunars/queries'
import type {
  AuthProvidersResponseDto,
  AvatarResponseDto,
  LoginMethodsResponseDto,
  OAuthLinkStartResponseDto,
  PersonalArcanaResponseDto,
  ProfileResponseDto,
  TelegramLoginCodeResponseDto,
  TelegramLoginExchangeResponseDto,
} from '@lunary/api-client'

const PROFILE_KEY = ['profile'] as const

export function useProfile(enabled: boolean) {
  return useQuery({
    queryKey: PROFILE_KEY,
    queryFn: () => api.get<ProfileResponseDto>('/users/me'),
    enabled,
    retry: false,
  })
}

export function useAvatarActions() {
  const qc = useQueryClient()
  const done = () =>
    Promise.all([qc.invalidateQueries({ queryKey: PROFILE_KEY }), qc.invalidateQueries({ queryKey: ['orbit'] })])
  const upload = useMutation({
    mutationFn: (blob: Blob) => api.putBlob<AvatarResponseDto>('/users/me/avatar', blob),
    onSuccess: done,
  })
  const choose = useMutation({
    mutationFn: (choice: AvatarChoiceBody) => api.patch<AvatarResponseDto>('/users/me/avatar', { choice }),
    onSuccess: done,
  })
  const remove = useMutation({
    mutationFn: () => api.delete<AvatarResponseDto>('/users/me/avatar'),
    onSuccess: done,
  })
  return { upload, choose, remove }
}

type AvatarChoiceBody = 'auto' | 'none' | 'upload' | 'telegram' | 'yandex' | 'google'

export function useRenameProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (displayName: string) => api.patch<ProfileResponseDto>('/users/me', { display_name: displayName }),
    onSuccess: (data) => qc.setQueryData(PROFILE_KEY, data),
  })
}

export function useSetTheme() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (theme: ThemeName | null) => api.patch<ProfileResponseDto>('/users/me/theme', { theme }),
    onSuccess: (data) => qc.setQueryData(PROFILE_KEY, data),
  })
}

export function useSetBirth() {
  const qc = useQueryClient()
  const refreshLunars = useRefreshLunars()
  return useMutation({
    mutationFn: (input: { day: number; month: number }) =>
      api.put<PersonalArcanaResponseDto>('/users/me/personal-arcana', input),
    onSuccess: () => {
      trackGoal('personal_arcana_set')
      refreshLunars()
      return qc.invalidateQueries({ queryKey: PROFILE_KEY })
    },
  })
}

export function createTelegramLoginCode() {
  return api.post<TelegramLoginCodeResponseDto>('/auth/telegram/login-codes', undefined)
}

export function exchangeTelegramLoginCode(code: string, pollSecret: string) {
  return api.post<TelegramLoginExchangeResponseDto>('/auth/telegram/login-codes/exchange', {
    code,
    poll_secret: pollSecret,
  })
}

const LOGIN_METHODS_KEY = ['login-methods'] as const

export function useLoginMethods() {
  return useQuery({
    queryKey: LOGIN_METHODS_KEY,
    queryFn: () => api.get<LoginMethodsResponseDto>('/users/me/identities'),
    retry: false,
  })
}

export function useUnlinkMethod() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (provider: 'telegram' | OAuthProviderKey) => api.delete<void>(`/users/me/identities/${provider}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LOGIN_METHODS_KEY })
      void qc.invalidateQueries({ queryKey: PROFILE_KEY })
    },
  })
}

export function useRefreshLoginMethods() {
  const qc = useQueryClient()
  const refreshLunars = useRefreshLunars()
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: LOGIN_METHODS_KEY })
    refreshLunars()
  }, [qc, refreshLunars])
}

export type OAuthProviderKey = 'yandex' | 'google'

export function startOAuthLink(provider: OAuthProviderKey) {
  return api.post<OAuthLinkStartResponseDto>(`/users/me/identities/${provider}`, undefined)
}

export function useAuthProviders() {
  return useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => api.get<AuthProvidersResponseDto>('/auth/providers'),
    staleTime: Infinity,
    retry: false,
  })
}

export function createTelegramLinkCode() {
  return api.post<TelegramLoginCodeResponseDto>('/users/me/identities/telegram', undefined)
}
